import { Coords, NESWBounds } from 'google-map-react';

/**
 * Checks if lat/lng pair is valid.
 *
 * NOTE: technically 0, 0 is a valid lat/lng pair but we don't have venues in that location
 * and often this pair means there was an error geocoding venues so we filter them out.
 *
 * @param venue Venue data
 */
export const geopointValid = (coords?: Coords) => {
    if (!coords) {
        return false;
    }

    const { lat, lng } = coords;
    return !!lat && !!lng;
};

/**
 * Given a list of coordinates, give a set of bounds that can be used to set the center and zoom
 * level of the map
 *
 * @param coords coordinates for all the annotations on the map
 * @param margin % of margin to add to the theoretical rectangle that fits all the components in the array
 */
export const getBounds = (coords: Coords[], margin: number = 0.1): NESWBounds => {
    const north = Math.max(...coords.map((c) => c.lat));
    const south = Math.min(...coords.map((c) => c.lat));
    const east = Math.max(...coords.map((c) => c.lng));
    const west = Math.min(...coords.map((c) => c.lng));

    // if ne == sw or there is only one point with coords given
    // set deltas to some non-zero values to avoid issue with `fitBounds` where returned data used
    // see https://github.com/google-map-react/google-map-react/issues/207 for details
    const lngDelta = Math.abs(south - north) || 0.5;
    const latDelta = Math.abs(east - west) || 0.5;

    return {
        ne: {
            lat: north + margin * latDelta,
            lng: east + margin * lngDelta,
        },
        sw: {
            lat: south - margin * latDelta,
            lng: west - margin * lngDelta,
        },
    };
};

/**
 * Given bounds and padding, return a new LatLngBounds object that is scaled by the padding value.
 * Padding should be a number between 0 and 1
 * @param bounds point to scale
 * @param scale scale
 */
export const padLatLng = (bounds: google.maps.LatLngBounds, scale: number) => {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latDelta = ne.lat() - sw.lat();
    const lngDelta = ne.lng() - sw.lng();

    const paddedNe = new google.maps.LatLng(ne.lat() - latDelta * scale, ne.lng() - lngDelta * scale);
    const paddedSw = new google.maps.LatLng(sw.lat() + latDelta * scale, sw.lng() + lngDelta * scale);

    return new google.maps.LatLngBounds(paddedSw, paddedNe);
};
